import { uiText, useUiLanguage } from '../../../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
  atsAnalyticsApi,
  buildDefaultAnalyticsViewConfig,
  getAnalyticsMetricLabel
} from '../../../../services/atsAnalyticsService';
import UnavailableVisual from '../../visuals/UnavailableVisual';
import RecruitmentFilterBar from '../AnalyticsRecruitment/components/RecruitmentFilterBar';
import WidgetShell from '../AnalyticsRecruitment/components/WidgetShell';
import {
  REPORT_CENTER_TEMPLATES,
  buildAnalyticsWidgetQueryFilters,
  buildReportCenterExportDocument,
  getReportCenterTemplate,
  getReportCenterTemplateSections,
  getReportCenterWidgetRequests
} from '../../analyticsExportDocuments';

const DEFAULT_JOB_STATUSES = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'];
const CLOSED_JOB_STATUSES = ['closed', 'unpublished', 'pending_approval', 'draft'];
const EMPTY_LIST = [];
const LONG_RANGE_CACHE_THRESHOLD_DAYS = 365;

const FILTER_IDS = {
  PERIOD: 'period',
  JOB_TITLE: 'jobTitle',
  LIFECYCLE: 'lifecycle',
  TEMPLATE: 'template',
  QUESTION: 'question',
  METRIC: 'metric'
};

const COPY = {
  en: {
    refresh: 'Refresh preview',
    refreshing: 'Refreshing',
    exportPdf: 'Export PDF',
    exportPptx: 'Export PPTX',
    exporting: 'Exporting',
    exported: 'Report exported.',
    dateRange: 'Period',
    generated: 'Generated',
    job: 'Job',
    lifecycle: 'Lifecycle',
    allJobs: 'All jobs',
    allStatuses: 'All statuses',
    activeJobs: 'Active jobs',
    closedJobs: 'Closed jobs',
    deletedJobs: 'Deleted jobs',
    question: 'Report',
    metric: 'Rank by',
    item: 'Item',
    value: 'Value',
    context: 'Context',
    preview: 'Report preview',
    focusedPreview: 'Focused section',
    reportCenter: 'Report Center',
    template: 'Template',
    sections: 'Sections',
    enabled: 'Enabled',
    sectionPreview: 'Section preview',
    noSections: 'Select at least one section to preview the report.',
    reportPack: 'Report pack',
    roleRisk: 'Role risk',
    sourcePerformance: 'Source performance',
    funnelConversion: 'Funnel conversion',
    jobPerformance: 'Job performance',
    offerHealth: 'Offer health',
    offerFunnel: 'Offer funnel',
    offersSubmitted: 'Offers submitted',
    offersApproved: 'Offers approved',
    offersSent: 'Offers sent',
    pipelineBacklog: 'Pipeline backlog',
    activeCandidates: 'Active candidates',
    alertsTargets: 'Alerts & targets',
    currentStatus: 'Current status',
    agingRoles: 'Aging roles',
    previewEmpty: 'No report rows available for this setup yet.',
    kpis: 'Selected KPIs',
    savedReports: 'Saved reports',
    dataConfidence: 'Data confidence',
    dataUsed: 'Data used',
    verifiedEvents: 'Verified event share',
    historicalSnapshotFill: 'Historical snapshot fill',
    historicalSnapshotNote: 'Historical fill keeps older ATS history usable; stakeholder exports still show coverage and warnings.',
    personalViews: 'Personal views',
    sharedTemplates: 'Shared templates',
    selectNone: 'None selected',
    personalViewName: 'Personal view name',
    sharedTemplateName: 'Shared template name',
    saveView: 'Save view',
    saveTemplate: 'Save template',
    viewSaved: 'View saved.',
    templateSaved: 'Template saved.',
    loadFailed: 'Report preview could not be loaded.',
    enterName: 'Enter a report name first.',
    includeNarrative: 'Include AI narrative in exports',
    table: 'Table',
    bar: 'Bar',
    widgetSettings: 'Widget settings',
    chartType: 'Chart type',
    groupBy: 'Group by',
    cardSize: 'Card size',
    resize: 'Resize widget',
    compact: 'Compact',
    medium: 'Medium',
    'three-quarter': 'Three quarter',
    large: 'Large',
    applications: 'Applications',
    hires: 'Hires',
    daysOpen: 'Days open',
    activePipeline: 'Active pipeline',
    dataCoverage: 'Data coverage',
    observedEvents: 'Observed events',
    reportWarnings: 'Coverage warnings',
    noWarnings: 'No data quality warnings for this report.',
    noActiveAlerts: 'No active target breaches',
    noSavedTargets: 'No saved targets configured',
    legacyViews: 'Legacy views excluded',
    missingSubjects: 'Subjects missing application event',
    sources: {
      applications: 'Applications and sources',
      movement: 'Stage movement and funnel',
      jobs: 'Job lifecycle and role metadata',
      hires: 'Hires, offers, and onboarding handoff'
    },
    sourceDetails: {
      applications: 'Applicants writes application_created plus applicant_index/by_job fields: candidateId, jobId, jobTitle, primarySource, sourceCategory, sourceTags.',
      movement: 'ATS board moves write applicant_stage_transitioned with from/to stageId and substageId, plus recruiter and source context.',
      jobs: 'Home job lifecycle writes job_created, job_published, job_closed, job_deleted, and posts lifecycleStatus/firstPublishedAt.',
      hires: 'Offer, hired, and onboarding handoffs write offer_accepted, candidate_hired, onboarding_launched_from_offer, onboarding, and employee records.'
    },
    questionSchema: {
      jobs: 'series.jobHealth: jobTitle, lifecycleStatus, applications, activeCandidates, stalledCandidates, untouchedApplicants, lateStageLosses, daysOpen.',
      sources: 'series.sourceEfficiency: source, applications, hires, hireRatePct, applicantSharePct, hireSharePct, efficiencyIndex.',
      funnel: 'series.stageConversions: fromCount, toCount, dropoffCount, conversionRatePct across applications, qualified, interviews, offers, hires.',
      aging: 'Pipeline & Risk open_roles_risk when available, then series.jobHealth/operationalSignals aging rows: jobTitle, lifecycleStatus, daysOpen, pipelineDepth, applications, hires.'
    },
    questions: {
      jobs: 'Jobs needing attention',
      sources: 'Source performance',
      funnel: 'Funnel drop-off',
      aging: 'Aging open roles'
    },
    metricLabels: {
      applications: 'Applications',
      hires: 'Hires',
      hireRatePct: 'Hire Rate',
      efficiencyIndex: 'Efficiency Index',
      conversionRatePct: 'Conversion Rate',
      dropoffCount: 'Drop-off Count',
      daysOpen: 'Days Open',
      riskScore: 'Risk Score',
      pipelineDepth: 'Pipeline Depth',
      count: 'Total',
      offers: 'Offers',
      alerts: 'Alerts',
      stalledCandidates: 'Stalled Candidates',
      applicationToViewRatePct: 'Application-to-View Rate',
      interviewToOfferRatePct: 'Interview-to-Offer Rate',
      offerApprovalRatePct: 'Offer Approval Rate',
      activePipelineSharePct: 'Active Pipeline Share'
    },
    questionMetricLabels: {
      'offer-health': {
        count: 'Offer funnel'
      },
      'pipeline-backlog': {
        count: 'Active candidates'
      },
      'alerts-targets': {
        count: 'Current status'
      }
    }
  },
  ar: {
    refresh: 'تحديث المعاينة',
    refreshing: 'جار التحديث',
    exportPdf: 'تصدير PDF',
    exportPptx: 'تصدير PPTX',
    exporting: 'جار التصدير',
    exported: 'تم تصدير التقرير.',
    dateRange: 'الفترة',
    generated: 'تاريخ الإنشاء',
    job: 'الوظيفة',
    lifecycle: 'حالة الوظيفة',
    allJobs: 'كل الوظائف',
    allStatuses: 'كل الحالات',
    activeJobs: 'الوظائف النشطة',
    closedJobs: 'الوظائف المغلقة',
    deletedJobs: 'الوظائف المحذوفة',
    question: 'التقرير',
    metric: 'الترتيب حسب',
    item: 'العنصر',
    value: 'القيمة',
    context: 'السياق',
    preview: 'معاينة التقرير',
    focusedPreview: 'القسم المحدد',
    reportCenter: 'مركز التقارير',
    template: 'القالب',
    sections: 'الأقسام',
    enabled: 'مفعل',
    sectionPreview: 'معاينة القسم',
    noSections: 'اختر قسماً واحداً على الأقل لمعاينة التقرير.',
    reportPack: 'حزمة التقرير',
    roleRisk: 'مخاطر الوظائف',
    sourcePerformance: 'أداء المصادر',
    funnelConversion: 'تحويل المسار',
    jobPerformance: 'أداء الوظائف',
    offerHealth: 'صحة العروض',
    offerFunnel: 'مسار العروض',
    offersSubmitted: 'العروض المقدمة',
    offersApproved: 'العروض الموافق عليها',
    offersSent: 'العروض المرسلة',
    pipelineBacklog: 'تراكم المسار',
    activeCandidates: 'المرشحون النشطون',
    alertsTargets: 'التنبيهات والأهداف',
    currentStatus: 'الحالة الحالية',
    agingRoles: 'الوظائف المتقادمة',
    previewEmpty: 'لا توجد صفوف متاحة لهذا الإعداد حالياً.',
    kpis: 'المؤشرات المختارة',
    savedReports: 'التقارير المحفوظة',
    dataConfidence: 'ثقة البيانات',
    dataUsed: 'البيانات المستخدمة',
    verifiedEvents: 'نسبة الأحداث المؤكدة',
    historicalSnapshotFill: 'تعبئة السجل التاريخي',
    historicalSnapshotNote: 'تعبئة السجل التاريخي تحافظ على قابلية استخدام بيانات ATS القديمة، مع عرض التغطية والملاحظات في التصدير.',
    personalViews: 'العروض الشخصية',
    sharedTemplates: 'القوالب المشتركة',
    selectNone: 'لا يوجد',
    personalViewName: 'اسم العرض الشخصي',
    sharedTemplateName: 'اسم القالب المشترك',
    saveView: 'حفظ العرض',
    saveTemplate: 'حفظ القالب',
    viewSaved: 'تم حفظ العرض.',
    templateSaved: 'تم حفظ القالب.',
    loadFailed: 'تعذر تحميل معاينة التقرير.',
    enterName: 'أدخل اسم التقرير أولاً.',
    includeNarrative: 'تضمين ملخص ذكي في التصدير',
    table: 'جدول',
    bar: 'أعمدة',
    widgetSettings: 'إعدادات العنصر',
    chartType: 'نوع الرسم',
    groupBy: 'التجميع حسب',
    cardSize: 'حجم البطاقة',
    resize: 'تغيير حجم العنصر',
    compact: 'مصغر',
    medium: 'متوسط',
    'three-quarter': 'ثلاثة أرباع',
    large: 'كبير',
    applications: 'المتقدمون',
    hires: 'التعيينات',
    daysOpen: 'أيام مفتوحة',
    activePipeline: 'المسار النشط',
    dataCoverage: 'تغطية البيانات',
    observedEvents: 'الأحداث المرصودة',
    reportWarnings: 'ملاحظات التغطية',
    noWarnings: 'لا توجد ملاحظات جودة بيانات لهذا التقرير.',
    noActiveAlerts: 'لا توجد تنبيهات أهداف نشطة',
    noSavedTargets: 'لا توجد أهداف محفوظة',
    legacyViews: 'مشاهدات قديمة مستبعدة',
    missingSubjects: 'مرشحون دون حدث تقديم',
    sources: {
      applications: 'المتقدمون والمصادر',
      movement: 'حركة المراحل ومسار التوظيف',
      jobs: 'حالة الوظائف وبيانات الدور',
      hires: 'التعيينات والعروض وتسليم الإعداد'
    },
    sourceDetails: {
      applications: 'صفحة المتقدمين تكتب application_created وحقول applicant_index/by_job: candidateId وjobId وjobTitle وprimarySource وsourceCategory وsourceTags.',
      movement: 'تحريك المرشحين في لوحة ATS يكتب applicant_stage_transitioned مع from/to stageId وsubstageId وسياق مسؤول التوظيف والمصدر.',
      jobs: 'صفحة الوظائف تكتب job_created وjob_published وjob_closed وjob_deleted وحقول lifecycleStatus وfirstPublishedAt في المنشورات.',
      hires: 'مسار العروض والتعيين والإعداد يكتب offer_accepted وcandidate_hired وonboarding_launched_from_offer وسجلات onboarding وemployees.'
    },
    questionSchema: {
      jobs: 'series.jobHealth: jobTitle وlifecycleStatus وapplications وactiveCandidates وstalledCandidates وuntouchedApplicants وlateStageLosses وdaysOpen.',
      sources: 'series.sourceEfficiency: source وapplications وhires وhireRatePct وapplicantSharePct وhireSharePct وefficiencyIndex.',
      funnel: 'series.stageConversions: fromCount وtoCount وdropoffCount وconversionRatePct عبر المتقدمين والمؤهلين والمقابلات والعروض والتعيينات.',
      aging: 'open_roles_risk من قسم المسار والمخاطر عند توفره، ثم series.jobHealth/operationalSignals: jobTitle وlifecycleStatus وdaysOpen وpipelineDepth وapplications وhires.'
    },
    questions: {
      jobs: 'الوظائف التي تحتاج انتباهاً',
      sources: 'أداء المصادر',
      funnel: 'تسرب مسار التوظيف',
      aging: 'الوظائف المفتوحة المتقادمة'
    },
    metricLabels: {
      applications: 'المتقدمون',
      hires: 'التعيينات',
      hireRatePct: 'معدل التعيين',
      efficiencyIndex: 'مؤشر الفعالية',
      conversionRatePct: 'معدل التحويل',
      dropoffCount: 'عدد التسرب',
      daysOpen: 'أيام مفتوحة',
      riskScore: 'درجة المخاطر',
      pipelineDepth: 'عمق المسار',
      count: 'الإجمالي',
      offers: 'العروض',
      alerts: 'التنبيهات',
      stalledCandidates: 'المرشحون المتعثرون',
      applicationToViewRatePct: 'معدل التقديم إلى المشاهدة',
      interviewToOfferRatePct: 'معدل المقابلة إلى العرض',
      offerApprovalRatePct: 'معدل الموافقة على العروض',
      activePipelineSharePct: 'حصة المسار النشط'
    },
    questionMetricLabels: {
      'offer-health': {
        count: 'مسار العروض'
      },
      'pipeline-backlog': {
        count: 'المرشحون النشطون'
      },
      'alerts-targets': {
        count: 'الحالة الحالية'
      }
    }
  }
};

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDisplayLabel = (value = '') =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatMetricValue = (metricKey, value) => {
  const normalized = safeNumber(value);
  const rounded = Math.round(normalized * 10) / 10;
  const lowerKey = String(metricKey || '').toLowerCase();

  if (metricKey === 'efficiencyIndex') {
    return `${rounded > 0 ? '+' : ''}${rounded}`;
  }

  if (metricKey === 'riskScore') {
    return `${rounded}%`;
  }

  if (lowerKey.includes('pct') || lowerKey.includes('rate')) {
    return `${rounded}%`;
  }

  if (lowerKey.includes('day')) {
    return `${rounded}d`;
  }

  return String(rounded);
};

const getQuestionOptions = (copy) => [
  {
    value: 'jobs-needing-attention',
    label: copy.questions.jobs,
    metricOptions: ['riskScore', 'stalledCandidates', 'untouchedApplicants', 'lateStageLosses', 'applications']
  },
  {
    value: 'best-sources',
    label: copy.questions.sources,
    metricOptions: ['efficiencyIndex', 'hireRatePct', 'hires', 'applications']
  },
  {
    value: 'funnel-dropoff',
    label: copy.questions.funnel,
    metricOptions: ['dropoffCount', 'conversionRatePct']
  },
  {
    value: 'aging-open-roles',
    label: copy.questions.aging,
    metricOptions: ['daysOpen', 'pipelineDepth', 'applications']
  },
  {
    value: 'job-performance',
    label: copy.jobPerformance,
    metricOptions: ['applications', 'hires', 'applicationToViewRatePct']
  },
  {
    value: 'offer-health',
    label: copy.offerHealth,
    metricOptions: ['count']
  },
  {
    value: 'pipeline-backlog',
    label: copy.pipelineBacklog,
    metricOptions: ['count']
  },
  {
    value: 'alerts-targets',
    label: copy.alertsTargets,
    metricOptions: ['count']
  }
];

const getMetricLabel = (metricKey, copy, questionKey = '') =>
  copy.questionMetricLabels?.[questionKey]?.[metricKey] || copy.metricLabels[metricKey] || getAnalyticsMetricLabel(metricKey);

const getSelectedJobStatusValue = (statuses = []) => {
  if (!statuses.length || statuses.length >= DEFAULT_JOB_STATUSES.length) return 'all';
  if (
    CLOSED_JOB_STATUSES.some((status) => statuses.includes(status)) &&
    !statuses.includes('published') &&
    !statuses.includes('deleted')
  ) {
    return 'closed';
  }
  return statuses[0] || 'all';
};

const getStatusListForFilter = (value) => {
  if (value === 'all') return DEFAULT_JOB_STATUSES;
  if (value === 'closed') return CLOSED_JOB_STATUSES;
  return [value];
};

const mergeAnalyticsConfig = (previous, nextConfig = {}) => ({
  ...previous,
  ...nextConfig,
  filters: {
    ...(previous.filters || {}),
    ...(nextConfig.filters || {})
  },
  builder: {
    ...(previous.builder || {}),
    ...(nextConfig.builder || {})
  }
});

const getWidgetDataset = (widget, graphId) =>
  widget?.payload?.datasets?.[graphId] || widget?.datasets?.[graphId] || null;

const getOpenRoleRiskCategories = (dashboardWidgets = {}) => {
  const dataset = getWidgetDataset(dashboardWidgets.open_roles_risk || dashboardWidgets.openRolesRisk, 'bubble_chart');
  return Array.isArray(dataset?.categories) ? dataset.categories : [];
};

const calculateRoleRiskScore = (row = {}) => {
  const daysOpen = safeNumber(row.daysOpen);
  const stalled = safeNumber(row.stalledCandidates);
  const untouched = safeNumber(row.untouchedApplicants);
  const lateLosses = safeNumber(row.lateStageLosses);
  const active = safeNumber(row.activeCandidates);
  const applications = safeNumber(row.applications);
  const raw = (daysOpen * 0.7) + (stalled * 14) + (untouched * 8) + (lateLosses * 18) - (active * 2) + (applications ? 0 : 12);
  return Math.max(0, Math.min(100, Math.round(raw)));
};

const normalizeOpenRoleRiskRows = ({ report, dashboardWidgets = {}, metric = 'riskScore', copy }) => {
  const categories = getOpenRoleRiskCategories(dashboardWidgets);
  const dashboardMetricKeys = new Set(['daysOpen', 'pipelineDepth', 'progressedCandidates', 'stalledCandidates', 'lossCount', 'riskScore']);
  if (categories.length && dashboardMetricKeys.has(metric)) {
    return categories
      .map((category, index) => {
        const metrics = category?.metrics || {};
        return {
          key: category?.id || `open-role-${index}`,
          label: category?.label || 'Untitled role',
          value: safeNumber(metrics?.[metric]),
          meta: `${formatMetricValue('daysOpen', metrics.daysOpen)} ${copy.daysOpen} · ${formatMetricValue('pipelineDepth', metrics.pipelineDepth)} ${getMetricLabel('pipelineDepth', copy)} · ${formatMetricValue('stalledCandidates', metrics.stalledCandidates)} ${getMetricLabel('stalledCandidates', copy)}`,
          data: category
        };
      })
      .sort((left, right) => right.value - left.value)
      .slice(0, 10);
  }

  return (report?.series?.jobHealth || [])
    .map((row, index) => ({
      key: row?.jobId || row?.jobTitle || `job-risk-${index}`,
      label: row?.jobTitle || 'Untitled role',
      value: metric === 'riskScore' ? calculateRoleRiskScore(row) : safeNumber(row?.[metric]),
      meta: `${formatMetricValue('daysOpen', row?.daysOpen)} ${copy.daysOpen} · ${formatMetricValue('activeCandidates', row?.activeCandidates)} ${copy.activePipeline} · ${formatMetricValue('stalledCandidates', row?.stalledCandidates)} ${getMetricLabel('stalledCandidates', copy)}`,
      data: row
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 10);
};

const normalizeAgingRoleRows = ({ report, dashboardWidgets = {}, metric = 'daysOpen', copy }) => {
  const categories = getOpenRoleRiskCategories(dashboardWidgets);
  const dashboardMetricKeys = new Set(['daysOpen', 'pipelineDepth', 'progressedCandidates', 'stalledCandidates', 'lossCount', 'riskScore']);
  if (categories.length && dashboardMetricKeys.has(metric)) {
    return categories
      .map((category, index) => {
        const metrics = category?.metrics || {};
        return {
          key: category?.id || `aging-open-role-${index}`,
          label: category?.label || 'Untitled role',
          value: safeNumber(metrics?.[metric] ?? metrics.daysOpen),
          meta: `${formatMetricValue('daysOpen', metrics.daysOpen)} ${copy.daysOpen} · ${formatMetricValue('pipelineDepth', metrics.pipelineDepth)} ${getMetricLabel('pipelineDepth', copy)} · ${formatMetricValue('riskScore', metrics.riskScore)} ${getMetricLabel('riskScore', copy)}`,
          data: category
        };
      })
      .sort((left, right) => safeNumber(right.data?.metrics?.daysOpen) - safeNumber(left.data?.metrics?.daysOpen))
      .slice(0, 10);
  }

  const strictAgingJobs = report?.operationalSignals?.agingOpenJobs || [];
  const fallbackJobs = strictAgingJobs.length
    ? strictAgingJobs
    : (report?.series?.jobHealth || [])
        .filter((row) => String(row?.lifecycleStatus || '').toLowerCase() === 'published' && safeNumber(row?.daysOpen) > 0);

  return fallbackJobs
    .map((row, index) => ({
      key: row?.jobId || row?.jobTitle || `aging-${index}`,
      label: row?.jobTitle || 'Untitled role',
      value: safeNumber(row?.[metric] ?? (metric === 'pipelineDepth' ? row?.activeCandidates : row?.daysOpen)),
      meta: `${formatMetricValue('daysOpen', row?.daysOpen)} ${copy.daysOpen} · ${formatMetricValue('applications', row?.applications)} ${copy.applications} · ${formatMetricValue('hires', row?.hires)} ${copy.hires}`,
      data: row
    }))
    .sort((left, right) => safeNumber(right.data?.daysOpen) - safeNumber(left.data?.daysOpen))
    .slice(0, 10);
};

const normalizeJobPerformanceRows = ({ report, metric = 'applications', copy }) =>
  (report?.series?.jobPerformance || [])
    .map((row, index) => ({
      key: row?.jobId || row?.jobTitle || `job-performance-${index}`,
      label: row?.jobTitle || 'Untitled role',
      value: safeNumber(row?.[metric]),
      meta: `${formatMetricValue('hires', row?.hires)} ${copy.hires} · ${formatMetricValue('applicationToViewRatePct', row?.applicationToViewRatePct)} ${getMetricLabel('applicationToViewRatePct', copy)}`,
      data: row
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 10);

const normalizeOfferHealthRows = ({ report, copy }) => {
  const funnel = report?.funnel || {};
  const metrics = report?.metrics || {};
  return [
    {
      key: 'offers-submitted',
      label: copy.offersSubmitted,
      value: safeNumber(funnel.offers_submitted),
      meta: `${formatMetricValue('interviewToOfferRatePct', metrics.interviewToOfferRatePct)} ${getMetricLabel('interviewToOfferRatePct', copy)}`
    },
    {
      key: 'offers-approved',
      label: copy.offersApproved,
      value: safeNumber(funnel.offers_approved),
      meta: `${formatMetricValue('offerApprovalRatePct', metrics.offerApprovalRatePct)} ${getMetricLabel('offerApprovalRatePct', copy)}`
    },
    {
      key: 'offers-sent',
      label: copy.offersSent,
      value: safeNumber(funnel.offers_sent),
      meta: `${formatMetricValue('hires', funnel.hires)} ${copy.hires}`
    },
    {
      key: 'hires',
      label: copy.hires,
      value: safeNumber(funnel.hires),
      meta: `${formatMetricValue('hireRatePct', metrics.hireRatePct)} ${getMetricLabel('hireRatePct', copy)}`
    }
  ];
};

const normalizePipelineBacklogRows = ({ report, copy }) =>
  (report?.series?.pipelineLoad || [])
    .map((row, index) => ({
      key: row?.id || row?.label || `pipeline-load-${index}`,
      label: row?.label || formatDisplayLabel(row?.id || `Pipeline ${index + 1}`),
      value: safeNumber(row?.count),
      meta: `${formatMetricValue('activePipelineSharePct', row?.activePipelineSharePct)} ${getMetricLabel('activePipelineSharePct', copy)}`,
      data: row
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 10);

const normalizeAlertsTargetRows = ({ report, alerts = [], targets = [], copy }) => {
  const alertRows = (alerts.length ? alerts : report?.alerts || []).map((alert, index) => ({
    key: alert?.id || `alert-${index}`,
    label: alert?.title || alert?.message || `Alert ${index + 1}`,
    value: safeNumber(alert?.actual ?? 1),
    meta: alert?.message || `${alert?.metricKey || ''} ${alert?.comparator || ''} ${alert?.threshold ?? ''}`.trim(),
    data: alert
  }));

  if (alertRows.length) return alertRows.slice(0, 10);

  const targetRows = (targets.length ? targets : report?.targets || [])
    .map((target, index) => ({
      key: target?.id || `target-${index}`,
      label: target?.title || target?.metricKey || `Target ${index + 1}`,
      value: safeNumber(target?.threshold),
      meta: target?.message || target?.scope || ''
    }))
    .slice(0, 10);

  return targetRows.length
    ? targetRows
    : [{
      key: 'no-active-alerts',
      label: copy.noActiveAlerts,
      value: 0,
      meta: copy.noSavedTargets
    }];
};

const normalizeReportRows = ({ report, builderConfig, copy, dashboardWidgets = {}, alerts = [], targets = [] }) => {
  const question = builderConfig?.question || 'jobs-needing-attention';
  const metric = builderConfig?.metric || 'stalledCandidates';

  if (question === 'best-sources') {
    const sourceRows = report?.series?.sourceEfficiency?.length
      ? report.series.sourceEfficiency
      : report?.series?.sourceBreakdown || [];

    return sourceRows
      .map((row, index) => ({
        key: row?.source || `source-${index}`,
        label: row?.source || 'Unknown source',
        value: safeNumber(row?.[metric]),
        meta: `${formatMetricValue('applications', row?.applications)} ${copy.applications} · ${formatMetricValue('hires', row?.hires)} ${copy.hires}`,
        data: row
      }))
      .sort((left, right) => right.value - left.value)
      .slice(0, 10);
  }

  if (question === 'funnel-dropoff') {
    return (report?.series?.stageConversions || [])
      .map((row, index) => ({
        key: row?.id || `stage-${index}`,
        label: row?.fromLabel && row?.toLabel
          ? `${row.fromLabel} -> ${row.toLabel}`
          : row?.label || formatDisplayLabel(row?.id || `Stage ${index + 1}`),
        value: safeNumber(row?.[metric]),
        meta: `${formatMetricValue('dropoffCount', row?.dropoffCount)} ${getMetricLabel('dropoffCount', copy)} · ${formatMetricValue('conversionRatePct', row?.conversionRatePct)} ${getMetricLabel('conversionRatePct', copy)}`,
        data: row
      }))
      .sort((left, right) => right.value - left.value)
      .slice(0, 10);
  }

  if (question === 'aging-open-roles') {
    return normalizeAgingRoleRows({ report, dashboardWidgets, metric, copy });
  }

  if (question === 'job-performance') {
    return normalizeJobPerformanceRows({ report, metric, copy });
  }

  if (question === 'offer-health') {
    return normalizeOfferHealthRows({ report, copy });
  }

  if (question === 'pipeline-backlog') {
    return normalizePipelineBacklogRows({ report, copy });
  }

  if (question === 'alerts-targets') {
    return normalizeAlertsTargetRows({ report, alerts, targets, copy });
  }

  return normalizeOpenRoleRiskRows({ report, dashboardWidgets, metric, copy });
};

const getReportPackSections = ({ report, dashboardWidgets = {}, alerts = [], targets = [], copy }) => [
  {
    id: 'role-risk',
    title: copy.roleRisk,
    question: 'jobs-needing-attention',
    metric: 'riskScore',
    metricLabel: getMetricLabel('riskScore', copy),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'jobs-needing-attention', metric: 'riskScore' }
    }).slice(0, 5)
  },
  {
    id: 'source-performance',
    title: copy.sourcePerformance,
    question: 'best-sources',
    metric: 'efficiencyIndex',
    metricLabel: getMetricLabel('efficiencyIndex', copy),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'best-sources', metric: 'efficiencyIndex' }
    }).slice(0, 5)
  },
  {
    id: 'funnel-conversion',
    title: copy.funnelConversion,
    question: 'funnel-dropoff',
    metric: 'dropoffCount',
    metricLabel: getMetricLabel('dropoffCount', copy),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'funnel-dropoff', metric: 'dropoffCount' }
    }).slice(0, 5)
  },
  {
    id: 'job-performance',
    title: copy.jobPerformance,
    question: 'job-performance',
    metric: 'applications',
    metricLabel: getMetricLabel('applications', copy),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'job-performance', metric: 'applications' }
    }).slice(0, 5)
  },
  {
    id: 'offer-health',
    title: copy.offerHealth,
    question: 'offer-health',
    metric: 'count',
    metricLabel: getMetricLabel('count', copy, 'offer-health'),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'offer-health', metric: 'count' }
    }).slice(0, 5)
  },
  {
    id: 'pipeline-backlog',
    title: copy.pipelineBacklog,
    question: 'pipeline-backlog',
    metric: 'count',
    metricLabel: getMetricLabel('count', copy, 'pipeline-backlog'),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'pipeline-backlog', metric: 'count' }
    }).slice(0, 5)
  },
  {
    id: 'aging-roles',
    title: copy.agingRoles,
    question: 'aging-open-roles',
    metric: 'daysOpen',
    metricLabel: getMetricLabel('daysOpen', copy),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      copy,
      builderConfig: { question: 'aging-open-roles', metric: 'daysOpen' }
    }).slice(0, 5)
  },
  {
    id: 'alerts-targets',
    title: copy.alertsTargets,
    question: 'alerts-targets',
    metric: 'count',
    metricLabel: getMetricLabel('count', copy, 'alerts-targets'),
    rows: normalizeReportRows({
      report,
      dashboardWidgets,
      alerts,
      targets,
      copy,
      builderConfig: { question: 'alerts-targets', metric: 'count' }
    }).slice(0, 5)
  }
];

const getMenuCopy = (copy) => ({
  settings: copy.widgetSettings,
  chartType: copy.chartType,
  groupBy: copy.groupBy,
  cardSize: copy.cardSize,
  resize: copy.resize,
  compact: copy.compact,
  medium: copy.medium,
  'three-quarter': copy['three-quarter'],
  large: copy.large
});

const getDataSourceRows = (copy) => [
  { id: 'applications', label: copy.sources.applications, detail: copy.sourceDetails.applications },
  { id: 'movement', label: copy.sources.movement, detail: copy.sourceDetails.movement },
  { id: 'jobs', label: copy.sources.jobs, detail: copy.sourceDetails.jobs },
  { id: 'hires', label: copy.sources.hires, detail: copy.sourceDetails.hires }
];

const getQuestionSchemaRow = (question, copy) => {
  const schemaByQuestion = {
    'jobs-needing-attention': { id: 'jobs-schema', label: copy.questions.jobs, detail: copy.questionSchema.jobs },
    'best-sources': { id: 'sources-schema', label: copy.questions.sources, detail: copy.questionSchema.sources },
    'funnel-dropoff': { id: 'funnel-schema', label: copy.questions.funnel, detail: copy.questionSchema.funnel },
    'aging-open-roles': { id: 'aging-schema', label: copy.questions.aging, detail: copy.questionSchema.aging },
    'job-performance': { id: 'job-performance-schema', label: copy.jobPerformance, detail: 'series.jobPerformance: applications, qualified, interviews, offers, hires, rejections, removals, views, applicationToViewRatePct.' },
    'offer-health': { id: 'offer-health-schema', label: copy.offerHealth, detail: 'report.funnel and report.metrics: offers_submitted, offers_approved, offers_sent, hires, interviewToOfferRatePct, offerApprovalRatePct.' },
    'pipeline-backlog': { id: 'pipeline-backlog-schema', label: copy.pipelineBacklog, detail: 'series.pipelineLoad: current active candidates by stage/substage with activePipelineSharePct.' },
    'alerts-targets': { id: 'alerts-targets-schema', label: copy.alertsTargets, detail: 'report alerts and saved analytics targets from analytics/v2 targets and alerts endpoints.' }
  };
  return schemaByQuestion[question] || schemaByQuestion['jobs-needing-attention'];
};

const AnalyticsReportBuilder = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { language } = useLanguage();
  const copy = COPY[language === 'ar' ? 'ar' : 'en'];
  const [viewConfig, setViewConfig] = useState(() => buildDefaultAnalyticsViewConfig());
  const [report, setReport] = useState(null);
  const [dashboardWidgets, setDashboardWidgets] = useState({});
  const [riskHistory, setRiskHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [userViews, setUserViews] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [targets, setTargets] = useState([]);
  const [activeReportTemplateId, setActiveReportTemplateId] = useState(REPORT_CENTER_TEMPLATES[0]?.id || '');
  const [enabledReportSectionIds, setEnabledReportSectionIds] = useState(() => REPORT_CENTER_TEMPLATES[0]?.sectionIds || []);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [activeUserViewId, setActiveUserViewId] = useState('');
  const [viewDraftName, setViewDraftName] = useState('');
  const [templateDraftName, setTemplateDraftName] = useState('');
  const [includeAiNarrative, setIncludeAiNarrative] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState('');
  const [actionState, setActionState] = useState(null);

  const filters = viewConfig.filters || {};
  const builderConfig = viewConfig.builder || buildDefaultAnalyticsViewConfig().builder;
  const selectedJobIds = filters.jobIds || EMPTY_LIST;
  const selectedJobTitles = filters.jobTitles || EMPTY_LIST;
  const selectedJobStatuses = filters.jobStatuses || DEFAULT_JOB_STATUSES;
  const dateRangeDays = Number(filters.dateRangeDays || 30);
  const includeArchivedCandidates = filters.includeArchivedCandidates !== false;
  const includeRemovedCandidates = filters.includeRemovedCandidates !== false;
  const selectedSingleJobTitle = selectedJobTitles[0] || '';
  const selectedJobStatusValue = getSelectedJobStatusValue(selectedJobStatuses);
  const actorId = currentUser?.uid || analyticsActorId || 'system';

  const questionOptions = useMemo(() => getQuestionOptions(copy), [copy]);
  const activeQuestion = questionOptions.find((option) => option.value === builderConfig.question) || questionOptions[0];
  const activeMetricKey = activeQuestion.metricOptions.includes(builderConfig.metric)
    ? builderConfig.metric
    : activeQuestion.metricOptions[0];
  const activeReportTemplate = useMemo(
    () => getReportCenterTemplate(activeReportTemplateId),
    [activeReportTemplateId]
  );
  const reportTemplateSections = useMemo(
    () => getReportCenterTemplateSections(activeReportTemplate.id),
    [activeReportTemplate.id]
  );
  const menuCopy = useMemo(() => getMenuCopy(copy), [copy]);
  const dataSourceRows = useMemo(() => getDataSourceRows(copy), [copy]);
  const questionSchemaRow = useMemo(
    () => getQuestionSchemaRow(activeQuestion.value, copy),
    [activeQuestion.value, copy]
  );

  const visibleTemplates = useMemo(
    () => templates.filter((template) => !template?.archived && !template?.deletedAt),
    [templates]
  );
  const visibleUserViews = useMemo(
    () => userViews.filter((view) => !view?.archived && !view?.deletedAt),
    [userViews]
  );

  const jobTitleOptions = useMemo(() => {
    const fromCatalog = (report?.jobCatalog || []).map((job) => job?.jobTitle || job?.title).filter(Boolean);
    const fromPerformance = (report?.series?.jobPerformance || []).map((job) => job?.jobTitle).filter(Boolean);
    return Array.from(new Set([...fromCatalog, ...fromPerformance])).sort((left, right) => left.localeCompare(right));
  }, [report?.jobCatalog, report?.series?.jobPerformance]);

  const reportRows = useMemo(
    () => normalizeReportRows({
      report,
      builderConfig: { ...builderConfig, question: activeQuestion.value, metric: activeMetricKey },
      copy,
      dashboardWidgets,
      alerts,
      targets
    }),
    [activeMetricKey, activeQuestion.value, alerts, builderConfig, copy, dashboardWidgets, report, targets]
  );
  const reportPackSections = useMemo(
    () => getReportPackSections({ report, dashboardWidgets, alerts, targets, copy }),
    [alerts, copy, dashboardWidgets, report, targets]
  );
  const reportCenterDocument = useMemo(
    () => buildReportCenterExportDocument({
      templateId: activeReportTemplate.id,
      enabledSectionIds: enabledReportSectionIds,
      report,
      dashboardWidgets,
      riskHistory,
      filterState: viewConfig.filters,
      alerts,
      targets,
      companyName: currentUser?.displayName || currentUser?.email || 'JOBSEEQR Employer'
    }),
    [activeReportTemplate.id, alerts, currentUser?.displayName, currentUser?.email, dashboardWidgets, enabledReportSectionIds, report, riskHistory, targets, viewConfig.filters]
  );
  const maxReportRowValue = useMemo(
    () => Math.max(...reportRows.map((row) => row.value), 0),
    [reportRows]
  );
  const metricCards = useMemo(() => {
    const metrics = report?.metrics || {};
    return (viewConfig.kpis || []).slice(0, 6).map((metricKey) => ({
      key: metricKey,
      label: getMetricLabel(metricKey, copy),
      value: formatMetricValue(metricKey, metrics[metricKey])
    }));
  }, [copy, report?.metrics, viewConfig.kpis]);
  const filterModels = useMemo(() => {
    const periodOptions = [30, 60, 90, 180, 365, 730].map((days) => ({
      id: String(days),
      label: days === 365 ? '12 months' : days === 730 ? '24 months' : uiText("{{count}} days", { count: days })
    }));
    const lifecycleOptions = [
      { id: 'all', label: copy.allStatuses },
      { id: 'published', label: copy.activeJobs },
      { id: 'closed', label: copy.closedJobs },
      { id: 'deleted', label: copy.deletedJobs }
    ];
    return [
      {
        id: FILTER_IDS.PERIOD,
        label: copy.dateRange,
        value: periodOptions.find((option) => option.id === String(dateRangeDays))?.label || uiText("{{count}} days", { count: dateRangeDays }),
        selectedId: String(dateRangeDays),
        selectedIds: [String(dateRangeDays)],
        options: periodOptions
      },
      {
        id: FILTER_IDS.JOB_TITLE,
        label: copy.job,
        value: selectedSingleJobTitle || copy.allJobs,
        selectedId: selectedSingleJobTitle || 'all',
        selectedIds: selectedSingleJobTitle ? [selectedSingleJobTitle] : [],
        options: [{ id: 'all', label: copy.allJobs }, ...jobTitleOptions.map((title) => ({ id: title, label: title }))]
      },
      {
        id: FILTER_IDS.LIFECYCLE,
        label: copy.lifecycle,
        value: lifecycleOptions.find((option) => option.id === selectedJobStatusValue)?.label || copy.allStatuses,
        selectedId: selectedJobStatusValue,
        selectedIds: selectedJobStatusValue === 'all' ? [] : [selectedJobStatusValue],
        options: lifecycleOptions
      },
      {
        id: FILTER_IDS.TEMPLATE,
        label: copy.template,
        value: activeReportTemplate.label,
        selectedId: activeReportTemplate.id,
        selectedIds: [activeReportTemplate.id],
        options: REPORT_CENTER_TEMPLATES.map((template) => ({ id: template.id, label: template.label }))
      }
    ];
  }, [
    activeReportTemplate.id,
    activeReportTemplate.label,
    copy,
    dateRangeDays,
    jobTitleOptions,
    selectedJobStatusValue,
    selectedSingleJobTitle
  ]);
  const toolbarActions = useMemo(() => [
    {
      id: 'refresh',
      label: loading ? copy.refreshing : copy.refresh,
      icon: loading ? 'fa-spinner fa-spin' : 'fa-rotate-right',
      tone: 'ghost'
    },
    {
      id: 'export_pdf',
      label: exporting && exportMode === 'pdf' ? copy.exporting : copy.exportPdf,
      icon: exporting && exportMode === 'pdf' ? 'fa-spinner fa-spin' : 'fa-file-pdf',
      tone: 'ghost'
    },
    {
      id: 'export_pptx',
      label: exporting && exportMode === 'pptx' ? copy.exporting : copy.exportPptx,
      icon: exporting && exportMode === 'pptx' ? 'fa-spinner fa-spin' : 'fa-file-powerpoint',
      tone: 'primary'
    }
  ], [copy, exportMode, exporting, loading]);
  const dataQualityRows = useMemo(() => {
    const dataQuality = report?.dataQuality || {};
    const excludedCounts = report?.excludedLegacyCounts || {};
    return [
      { id: 'coverage', label: copy.dataCoverage, value: dataQuality.coverageScore === null || dataQuality.coverageScore === undefined ? '--' : `${dataQuality.coverageScore}%` },
      { id: 'observed', label: copy.verifiedEvents, value: `${safeNumber(dataQuality.observedEventPct)}%` },
      { id: 'inferred', label: copy.historicalSnapshotFill, value: `${safeNumber(dataQuality.inferredEventPct)}%`, note: copy.historicalSnapshotNote },
      { id: 'legacy', label: copy.legacyViews, value: formatMetricValue('count', excludedCounts.legacyLifetimeJobViews) },
      { id: 'missing', label: copy.missingSubjects, value: formatMetricValue('count', excludedCounts.subjectsMissingApplicationEvent) }
    ];
  }, [copy, report?.dataQuality, report?.excludedLegacyCounts]);
  const reportWarnings = useMemo(
    () => [...(report?.reportWarnings || []), ...(report?.dataQuality?.warnings || [])].filter(Boolean),
    [report?.dataQuality?.warnings, report?.reportWarnings]
  );

  const announceAction = (tone, message) => {
    setActionState({ tone, message });
  };

  const updateFilters = (patch) => {
    setViewConfig((previous) => ({
      ...previous,
      filters: {
        ...previous.filters,
        ...patch
      }
    }));
  };

  const updateBuilderConfig = (patch) => {
    setViewConfig((previous) => ({
      ...previous,
      builder: {
        ...previous.builder,
        ...patch
      }
    }));
  };

  const loadReportBuilderData = useCallback(async ({ announceSuccess = false } = {}) => {
    if (!companyUID) return;

    setLoading(true);

    try {
      const widgetRequests = getReportCenterWidgetRequests(activeReportTemplate.id, enabledReportSectionIds);
      const widgetFilterState = {
        ...(viewConfig.filters || {}),
        dateRangeDays
      };
      const cachePayload = dateRangeDays >= LONG_RANGE_CACHE_THRESHOLD_DAYS
        ? await atsAnalyticsApi.getReportCache({
          companyUid: companyUID,
          scopeType: 'report_center',
          templateId: activeReportTemplate.id,
          dateRangeDays,
          sectionIds: enabledReportSectionIds,
          jobTitles: selectedJobTitles,
          jobStatuses: selectedJobStatuses
        }).catch(() => null)
        : null;
      const cachedDashboardWidgets = cachePayload?.dashboardWidgets || {};
      const cachedRiskHistory = Array.isArray(cachePayload?.riskHistory) ? cachePayload.riskHistory : [];
      const [
        reportPayload,
        templatesPayload,
        viewsPayload,
        alertsPayload,
        targetsPayload,
        widgetPayloads
      ] = await Promise.all([
        cachePayload?.report ? Promise.resolve(cachePayload.report) : atsAnalyticsApi.getReport({
          companyUid: companyUID,
          dateRangeDays,
          includeCompare: false,
          autoBackfill: false,
          jobIds: selectedJobIds,
          jobTitles: selectedJobTitles,
          jobStatuses: selectedJobStatuses,
          includeArchivedCandidates,
          includeRemovedCandidates
        }),
        atsAnalyticsApi.getTemplates(companyUID).catch(() => ({ templates: [] })),
        actorId ? atsAnalyticsApi.getUserViews(companyUID, actorId).catch(() => ({ views: [] })) : Promise.resolve({ views: [] }),
        atsAnalyticsApi.getAlerts(companyUID, dateRangeDays, actorId).catch(() => ({ alerts: [] })),
        atsAnalyticsApi.getTargets(companyUID).catch(() => ({ targets: [] })),
        Promise.all(
          widgetRequests
            .filter(({ widgetId }) => widgetId === 'open_roles_risk' || !cachedDashboardWidgets[widgetId])
            .map(({ screenId, widgetId }) =>
            atsAnalyticsApi
              .queryDashboardWidget(
                companyUID,
                screenId,
                widgetId,
                buildAnalyticsWidgetQueryFilters(widgetFilterState, screenId),
                '',
                { revision: `report-center:${activeReportTemplate.id}:${widgetId}` }
              )
              .then((response) => [widgetId, response?.widget || null])
              .catch(() => [widgetId, null])
            )
        )
      ]);

      setReport({
        ...reportPayload,
        alerts: alertsPayload?.alerts || [],
        targets: targetsPayload?.targets || []
      });
      setTemplates(templatesPayload?.templates || []);
      setUserViews(viewsPayload?.views || []);
      setAlerts(alertsPayload?.alerts || []);
      setTargets(targetsPayload?.targets || []);
      setRiskHistory(cachedRiskHistory.length ? cachedRiskHistory : Array.isArray(reportPayload?.riskHistory) ? reportPayload.riskHistory : []);
      setDashboardWidgets((widgetPayloads || []).reduce((accumulator, [widgetId, widget]) => {
        if (widget) accumulator[widgetId] = widget;
        return accumulator;
      }, { ...cachedDashboardWidgets }));

      if (announceSuccess) {
        announceAction('success', copy.refresh);
      }
    } catch (error) {
      console.error('Report builder load failed:', error);
      announceAction('error', error?.message || copy.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [
    actorId,
    activeReportTemplate.id,
    companyUID,
    copy.loadFailed,
    copy.refresh,
    dateRangeDays,
    enabledReportSectionIds,
    includeArchivedCandidates,
    includeRemovedCandidates,
    selectedJobIds,
    selectedJobStatuses,
    selectedJobTitles,
    viewConfig.filters
  ]);

  useEffect(() => {
    loadReportBuilderData();
  }, [loadReportBuilderData]);

  useEffect(() => {
    if (!actionState?.message) return undefined;
    const timeoutId = window.setTimeout(() => setActionState(null), actionState.tone === 'error' ? 6500 : 3600);
    return () => window.clearTimeout(timeoutId);
  }, [actionState]);

  const applySavedConfig = (config, type, id) => {
    if (!config) return;
    setViewConfig((previous) => mergeAnalyticsConfig(previous, config));
    setActiveTemplateId(type === 'template' ? id : '');
    setActiveUserViewId(type === 'view' ? id : '');
  };

  const handleFilterChange = (filterId, value) => {
    if (filterId === FILTER_IDS.PERIOD) {
      updateFilters({ dateRangeDays: Number(value) || 30 });
      return;
    }

    if (filterId === FILTER_IDS.JOB_TITLE) {
      updateFilters({ jobTitles: value && value !== 'all' ? [value] : [] });
      return;
    }

    if (filterId === FILTER_IDS.LIFECYCLE) {
      updateFilters({ jobStatuses: getStatusListForFilter(value) });
      return;
    }

    if (filterId === FILTER_IDS.TEMPLATE) {
      const nextTemplate = getReportCenterTemplate(value);
      setActiveReportTemplateId(nextTemplate.id);
      setEnabledReportSectionIds(nextTemplate.sectionIds);
      return;
    }

    if (filterId === FILTER_IDS.QUESTION) {
      const nextQuestion = questionOptions.find((item) => item.value === value) || questionOptions[0];
      updateBuilderConfig({
        question: nextQuestion.value,
        metric: nextQuestion.metricOptions[0],
        sortBy: nextQuestion.metricOptions[0]
      });
      return;
    }

    if (filterId === FILTER_IDS.METRIC) {
      updateBuilderConfig({ metric: value, sortBy: value });
    }
  };

  const saveCurrentAsUserView = async () => {
    const name = formatDisplayLabel(viewDraftName);
    if (!name) {
      announceAction('error', copy.enterName);
      return;
    }

    try {
      const response = await atsAnalyticsApi.saveUserView(
        companyUID,
        actorId,
        {
          name,
          config: viewConfig,
          schemaVersion: 1
        },
        activeUserViewId || null
      );
      if (response?.view?.id) setActiveUserViewId(response.view.id);
      setViewDraftName('');
      await loadReportBuilderData();
      announceAction('success', copy.viewSaved);
    } catch (error) {
      announceAction('error', error?.message || copy.loadFailed);
    }
  };

  const saveCurrentAsTemplate = async () => {
    const name = formatDisplayLabel(templateDraftName);
    if (!name) {
      announceAction('error', copy.enterName);
      return;
    }

    try {
      const response = await atsAnalyticsApi.saveTemplate(
        companyUID,
        {
          name,
          config: viewConfig,
          isPreset: false,
          schemaVersion: 1
        },
        activeTemplateId && !String(activeTemplateId).startsWith('preset_') ? activeTemplateId : null
      );
      if (response?.template?.id) setActiveTemplateId(response.template.id);
      setTemplateDraftName('');
      await loadReportBuilderData();
      announceAction('success', copy.templateSaved);
    } catch (error) {
      announceAction('error', error?.message || copy.loadFailed);
    }
  };

  const exportReport = async (format = 'pptx') => {
    if (!report?.metrics || exporting) return;

    try {
      setExporting(true);
      setExportMode(format);
      let aiNarrative = null;

      if (includeAiNarrative) {
        aiNarrative = await atsAnalyticsApi.generateAiNarrative({
          companyUid: companyUID,
          dateRangeDays,
          includeCompare: false,
          includeInsights: true,
          jobIds: selectedJobIds,
          jobTitles: selectedJobTitles,
          jobStatuses: selectedJobStatuses,
          includeArchivedCandidates,
          includeRemovedCandidates
        }).catch(() => null);
      }

      const exportService = await import('../../../../services/atsAnalyticsExport');
      const exportHandler = format === 'pdf'
        ? exportService.exportAtsAnalyticsDocumentPdf
        : exportService.exportAtsAnalyticsDocumentDeck;
      const narrativeText = aiNarrative?.narrative?.executive_summary || aiNarrative?.executive_summary || '';
      const exportDocument = includeAiNarrative && narrativeText
        ? {
          ...reportCenterDocument,
          sections: [
            {
              id: 'ai_narrative',
              title: copy.includeNarrative,
              description: activeReportTemplate.subtitle,
              type: 'narrative',
              items: [
                narrativeText,
                ...(aiNarrative?.narrative?.recommended_next_actions || []).slice(0, 4)
              ].filter(Boolean)
            },
            ...(reportCenterDocument.sections || [])
          ]
        }
        : reportCenterDocument;

      await exportHandler({
        document: exportDocument,
        language
      });
      announceAction('success', copy.exported);
    } catch (error) {
      console.error('Report builder export failed:', error);
      announceAction('error', error?.message || copy.loadFailed);
    } finally {
      setExporting(false);
      setExportMode('');
    }
  };

  const handleToolbarAction = (actionId) => {
    if (actionId === 'refresh') {
      void loadReportBuilderData({ announceSuccess: true });
      return;
    }

    if (actionId === 'export_pdf') {
      void exportReport('pdf');
      return;
    }

    if (actionId === 'export_pptx') {
      void exportReport('pptx');
    }
  };

  const toggleReportSection = (sectionId) => {
    setEnabledReportSectionIds((previous) => {
      if (previous.includes(sectionId)) {
        return previous.filter((id) => id !== sectionId);
      }
      return [...previous, sectionId];
    });
  };

  const renderRowsTable = ({ rows, metric, metricLabel = getMetricLabel(metric, copy), emptyMessage = copy.previewEmpty, compact = false }) => {
    if (!rows.length) {
      return <UnavailableVisual message={emptyMessage} />;
    }

    return (
      <div className={`analytics-visual analytics-visual-table-grid analytics-report-builder-table${compact ? ' compact' : ''}`}>
        <div className="analytics-visual-table-head analytics-report-builder-result-row">
          <div className="analytics-visual-table-head-label">{copy.item}</div>
          <div className="analytics-visual-table-head-cell analytics-report-builder-value-cell">{metricLabel}</div>
          <div className="analytics-visual-table-head-cell analytics-report-builder-context-cell">{copy.context}</div>
        </div>
        {rows.map((row) => (
          <div key={row.key} className="analytics-visual-table-row-grid analytics-report-builder-result-row">
            <div className="analytics-visual-table-row-label">{uiText(row.label)}</div>
            <div className="analytics-visual-table-cell analytics-report-builder-value-cell">{row.formattedValue ?? formatMetricValue(metric, row.value)}</div>
            <div className="analytics-visual-table-cell analytics-report-builder-context-cell">{row.meta}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderDocumentSectionPreview = (section) => {
    if (section.type === 'kpi_strip' || section.type === 'confidence_notes') {
      const metrics = section.metrics || [];
      return metrics.length ? (
        <>
          <div className="analytics-report-builder-kpi-strip" aria-label={uiText(section.title)}>
            {metrics.map((metric) => (
              <div key={metric.id} className="executive-overview-kpi analytics-report-builder-kpi-tile">
                <span className="executive-overview-kpi-label">{uiText(metric.label)}</span>
                <strong className="executive-overview-kpi-value">
                  <span className="executive-overview-kpi-value-main">{metric.value}</span>
                </strong>
              </div>
            ))}
          </div>
          {section.type === 'confidence_notes' && section.items?.length ? (
            <div className="analytics-report-builder-source-list">
              {section.items.map((item, index) => (
                <div key={`${section.id}-note-${index}`} className="analytics-report-builder-source-row">
                  <span className="analytics-visual-sub">{typeof item === 'string' ? item : item?.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : <UnavailableVisual message={copy.previewEmpty} />;
    }

    if (section.type === 'action_list' || section.type === 'narrative') {
      const items = section.items || [];
      return items.length ? (
        <div className="analytics-report-builder-source-list">
          {items.map((item, index) => {
            const label = typeof item === 'string' ? item : item?.label;
            const detail = typeof item === 'string' ? '' : item?.detail;
            return (
              <div key={`${section.id}-${index}`} className="analytics-report-builder-source-row">
                <span className="analytics-visual-label">{uiText(label)}</span>
                {detail ? <span className="analytics-visual-sub">{detail}</span> : null}
              </div>
            );
          })}
        </div>
      ) : <UnavailableVisual message={copy.previewEmpty} />;
    }

    const columns = Array.isArray(section.columns) ? section.columns : [];
    const rows = (section.rows || []).map((row, index) => ({
      key: row.id || `${section.id}-${index}`,
      label: row.label || row.values?.item || `Row ${index + 1}`,
      value: row.value ?? '',
      formattedValue: row.value === undefined || row.value === null || row.value === '' ? '-' : String(row.value),
      meta: columns
        .filter((column) => !['item', 'label'].includes(column.id))
        .map((column) => {
          const value = row.values?.[column.id];
          if (value === undefined || value === null || value === '') return '';
          return `${column.label}: ${value}`;
        })
        .filter(Boolean)
        .join(' · ')
    }));

    return renderRowsTable({
      rows,
      metric: 'count',
      metricLabel: copy.value,
      compact: true
    });
  };

  const renderResultRows = () => {
    if (!reportRows.length) {
      return <UnavailableVisual message={copy.previewEmpty} />;
    }

    if (builderConfig.chartType === 'bar') {
      return (
        <div className="analytics-visual analytics-visual-rows">
          {reportRows.map((row) => {
            const width = maxReportRowValue > 0 ? Math.max(4, (row.value / maxReportRowValue) * 100) : 0;
            return (
              <div key={row.key} className="analytics-visual-row">
                <div className="analytics-visual-row-head">
                  <span className="analytics-visual-label">{uiText(row.label)}</span>
                  <div className="analytics-visual-row-metrics">
                    <strong className="analytics-visual-number">{formatMetricValue(activeMetricKey, row.value)}</strong>
                    {row.meta ? <span className="analytics-visual-sub">{row.meta}</span> : null}
                  </div>
                </div>
                <div className="analytics-visual-track slim">
                  <span className="analytics-visual-fill accent" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return renderRowsTable({
      rows: reportRows,
      metric: activeMetricKey,
      metricLabel: getMetricLabel(activeMetricKey, copy, activeQuestion.value)
    });
  };

  return (
    <section className={`analytics-recruitment analytics-report-builder${isRTL ? ' rtl' : ''}`}>
      {loading && !report ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
          <RecruitmentFilterBar
            filters={filterModels}
            actions={toolbarActions}
            onAction={handleToolbarAction}
            onFilterChange={handleFilterChange}
            isRTL={isRTL}
          />

          {actionState?.message ? (
            <div className={`analytics-report-builder-alert ${actionState.tone}`} role="status">
              {actionState.message}
            </div>
          ) : null}

          <div className="analytics-recruitment-canvas">
            <WidgetShell
              id="report_center_setup"
              title={copy.reportCenter}
              annotationItems={[
                `${copy.generated}: ${report?.generatedAt ? new Date(report.generatedAt).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US') : '-'}`,
                activeReportTemplate.subtitle
              ]}
              size="large"
              variant="data-forward"
              onSizeChange={() => {}}
              allowResize={false}
              menuCopy={menuCopy}
              isRTL={isRTL}
            >
              <div className="analytics-report-builder-kpi-strip" aria-label={copy.kpis}>
                {metricCards.map((metric) => (
                  <div key={metric.key} className="executive-overview-kpi analytics-report-builder-kpi-tile">
                    <span className="executive-overview-kpi-label">{uiText(metric.label)}</span>
                    <strong className="executive-overview-kpi-value">
                      <span className="executive-overview-kpi-value-main">{metric.value}</span>
                    </strong>
                  </div>
                ))}
              </div>
              <div className="analytics-report-builder-section-toggle-grid" aria-label={copy.sections}>
                {reportTemplateSections.map((section) => (
                  <label key={section.id} className="analytics-report-builder-check analytics-report-builder-section-toggle">
                    <input
                      type="checkbox"
                      checked={enabledReportSectionIds.includes(section.id)}
                      onChange={() => toggleReportSection(section.id)}
                    />
                    <span>
                      <strong>{uiText(section.title)}</strong>
                      <small>{uiText(section.description)}</small>
                    </span>
                  </label>
                ))}
              </div>
            </WidgetShell>

            {reportCenterDocument.sections.length ? reportCenterDocument.sections.map((section) => (
              <WidgetShell
                key={section.id}
                id={`report_center_${section.id}`}
                title={uiText(section.title)}
                annotationItems={[section.description || copy.sectionPreview]}
                size="large"
                variant="data-forward"
                allowResize={false}
                menuCopy={menuCopy}
                isRTL={isRTL}
              >
                {renderDocumentSectionPreview(section)}
              </WidgetShell>
            )) : (
              <WidgetShell
                id="report_center_empty"
                title={copy.sectionPreview}
                annotationItems={[copy.noSections]}
                size="large"
                variant="contextual"
                allowResize={false}
                menuCopy={menuCopy}
                isRTL={isRTL}
              >
                <UnavailableVisual message={copy.noSections} />
              </WidgetShell>
            )}

            <WidgetShell
              id="saved_reports"
              title={copy.savedReports}
              annotationItems={[copy.includeNarrative]}
              size="medium"
              variant="contextual"
              allowResize={false}
              menuCopy={menuCopy}
              isRTL={isRTL}
            >
              <div className="analytics-report-builder-save-grid">
                <label className="analytics-report-builder-field">
                  <span>{copy.personalViews}</span>
                  <select
                    value={activeUserViewId}
                    onChange={(event) => {
                      const viewId = event.target.value;
                      const view = visibleUserViews.find((item) => String(item.id) === String(viewId));
                      setActiveUserViewId(viewId);
                      if (view) applySavedConfig(view.config, 'view', view.id);
                    }}
                  >
                    <option value="">{copy.selectNone}</option>
                    {visibleUserViews.map((view) => (
                      <option key={view.id} value={view.id}>{formatDisplayLabel(view.name)}</option>
                    ))}
                  </select>
                </label>
                <label className="analytics-report-builder-field">
                  <span>{copy.sharedTemplates}</span>
                  <select
                    value={activeTemplateId}
                    onChange={(event) => {
                      const templateId = event.target.value;
                      const template = visibleTemplates.find((item) => String(item.id) === String(templateId));
                      setActiveTemplateId(templateId);
                      if (template) applySavedConfig(template.config, 'template', template.id);
                    }}
                  >
                    <option value="">{copy.selectNone}</option>
                    {visibleTemplates.map((template) => (
                      <option key={template.id} value={template.id}>{formatDisplayLabel(template.name)}</option>
                    ))}
                  </select>
                </label>
                <label className="analytics-report-builder-field">
                  <span>{copy.personalViewName}</span>
                  <input
                    type="text"
                    value={viewDraftName}
                    onChange={(event) => setViewDraftName(event.target.value)}
                    placeholder={copy.personalViewName}
                  />
                </label>
                <button type="button" className="analytics-recruitment-action-button ghost" onClick={saveCurrentAsUserView}>
                  <i className="fa-solid fa-bookmark" />
                  <span>{copy.saveView}</span>
                </button>
                <label className="analytics-report-builder-field">
                  <span>{copy.sharedTemplateName}</span>
                  <input
                    type="text"
                    value={templateDraftName}
                    onChange={(event) => setTemplateDraftName(event.target.value)}
                    placeholder={copy.sharedTemplateName}
                  />
                </label>
                <button type="button" className="analytics-recruitment-action-button ghost" onClick={saveCurrentAsTemplate}>
                  <i className="fa-solid fa-layer-group" />
                  <span>{copy.saveTemplate}</span>
                </button>
                <label className="analytics-report-builder-check">
                  <input
                    type="checkbox"
                    checked={includeAiNarrative}
                    onChange={(event) => setIncludeAiNarrative(event.target.checked)}
                  />
                  <span>{copy.includeNarrative}</span>
                </label>
              </div>
            </WidgetShell>

            <WidgetShell
              id="data_confidence"
              title={copy.dataConfidence}
              annotationItems={reportWarnings.length ? [copy.reportWarnings, `${reportWarnings.length}`] : [copy.noWarnings]}
              size="medium"
              variant="contextual"
              allowResize={false}
              menuCopy={menuCopy}
              isRTL={isRTL}
            >
              <div className="analytics-visual analytics-visual-rows compact">
                {dataQualityRows.map((row) => (
                  <div key={row.id} className="analytics-visual-row">
                    <div className="analytics-visual-row-head">
                      <span className="analytics-visual-label">{uiText(row.label)}</span>
                      <strong className="analytics-visual-number">{row.value}</strong>
                    </div>
                    {row.note ? <span className="analytics-visual-sub">{row.note}</span> : null}
                  </div>
                ))}
              </div>
              <div className="analytics-report-builder-source-list">
                <div className="analytics-report-builder-source-row featured">
                  <span className="analytics-visual-label">{uiText(questionSchemaRow.label)}</span>
                  <span className="analytics-visual-sub">{questionSchemaRow.detail}</span>
                </div>
                {dataSourceRows.map((row) => (
                  <div key={row.id} className="analytics-report-builder-source-row">
                    <span className="analytics-visual-label">{uiText(row.label)}</span>
                    <span className="analytics-visual-sub">{row.detail}</span>
                  </div>
                ))}
              </div>
            </WidgetShell>
          </div>
        </>
      )}
    </section>
  );
};

export default AnalyticsReportBuilder;
